import { Controller } from "@hotwired/stimulus"
import { destroy, post, patch } from "@rails/request.js"

// Role Management Controller
// Handles role assignment/removal for admin user management
export default class extends Controller {
  static targets = [
    "assignmentForm",
    "rolesList",
    "adminActionForm",
    "adminActionDetails",
    "adminActionReason",
    "adminActionConfirm",
    "submitButton"
  ]

  static values = {
    userId: String,
    userName: String
  }

  connect() {
    this.currentAdminAction = null
    this.setupKeyboardListeners()
  }

  disconnect() {
    this.removeKeyboardListeners()
  }

  setupKeyboardListeners() {
    this.escapeHandler = (e) => {
      if (e.key === 'Escape') {
        if (!this.assignmentFormTarget.classList.contains('hidden')) {
          this.toggleRoleAssignment()
          e.preventDefault()
        } else if (!this.adminActionFormTarget.classList.contains('hidden')) {
          this.hideAdminActionForm()
          e.preventDefault()
        }
      }
    }
    document.addEventListener('keydown', this.escapeHandler)
  }

  removeKeyboardListeners() {
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler)
    }
  }

  // Toggle role assignment form visibility
  toggleRoleAssignment() {
    const form = this.assignmentFormTarget
    const assignForm = form.querySelector('#assign-role-form')
    
    if (form.classList.contains('hidden')) {
      this.showAssignmentForm(form, assignForm)
    } else {
      this.hideAssignmentForm(form)
    }
  }

  showAssignmentForm(form, assignForm) {
    // Show form with animation
    form.classList.remove('hidden')
    form.style.opacity = '0'
    form.style.transform = 'translateY(-10px)'
    
    // Reset form
    if (assignForm) {
      assignForm.reset()
      this.resetSubmitButton()
    }
    
    // Animate in
    setTimeout(() => {
      form.style.opacity = '1'
      form.style.transform = 'translateY(0)'
    }, 10)
    
    // Focus first input
    const roleSelect = form.querySelector('select[name="role_id"]')
    if (roleSelect) {
      setTimeout(() => roleSelect.focus(), 100)
    }
  }

  hideAssignmentForm(form) {
    // Hide form with animation
    form.style.opacity = '0'
    form.style.transform = 'translateY(-10px)'
    
    setTimeout(() => {
      form.classList.add('hidden')
      form.style.opacity = ''
      form.style.transform = ''
    }, 200)
  }

  resetSubmitButton() {
    const submitButton = this.element.querySelector('button[type="submit"]')
    if (submitButton) {
      submitButton.disabled = false
      const buttonText = submitButton.querySelector('.assign-role-text')
      const buttonLoading = submitButton.querySelector('.assign-role-loading')
      if (buttonText && buttonLoading) {
        buttonText.classList.remove('hidden')
        buttonLoading.classList.add('hidden')
      }
    }
  }

  // Remove role with confirmation
  removeRole(event) {
    event.preventDefault()
    
    const button = event.currentTarget
    const roleId = button.dataset.roleId
    const roleName = button.dataset.roleName

    if (!this.confirmRoleRemoval(roleName)) {
      return
    }

    this.setRemovalLoadingState(button)
    this.executeRoleRemoval(roleId, roleName, button)
  }

  confirmRoleRemoval(roleName) {
    return confirm(
      `Are you sure you want to remove the ${roleName} role from this user?\n\n` +
      `This action will immediately revoke the permissions associated with this role.`
    )
  }

  setRemovalLoadingState(button) {
    const roleElement = button.closest('[data-role-id]')
    if (roleElement) {
      roleElement.style.opacity = '0.6'
      button.disabled = true
      button.innerHTML = `
        <svg class="w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Removing...
      `
    }
  }

  async executeRoleRemoval(roleId, roleName, button) {
    try {
      const response = await destroy(`/admin/users/${this.userIdValue}/remove_role/${roleId}`, {
        responseKind: "json"
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.statusCode}`)
      }

      const data = await response.json

      if (data.success) {
        this.handleSuccessfulRemoval(button)
        this.showNotification(data.message, 'success')
      } else {
        this.resetRemovalButton(button)
        this.showNotification(data.error || 'Failed to remove role', 'error')
      }
    } catch (error) {
      console.error('Error:', error)
      this.resetRemovalButton(button)
      this.showNotification('An error occurred while removing the role. Please try again.', 'error')
    }
  }

  handleSuccessfulRemoval(button) {
    const roleElement = button.closest('[data-role-id]')
    if (roleElement) {
      // Animate removal
      roleElement.style.transform = 'translateX(100%)'
      roleElement.style.opacity = '0'
      
      setTimeout(() => {
        roleElement.remove()
        this.checkForEmptyRolesList()
      }, 300)
    }
  }

  checkForEmptyRolesList() {
    const remainingRoles = this.rolesListTarget.querySelectorAll('[data-role-id]')
    if (remainingRoles.length === 0) {
      this.rolesListTarget.innerHTML = `
        <div class="text-center py-8">
          <div class="w-12 h-12 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg class="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h3 class="text-sm font-medium text-gray-900 mb-1">No roles assigned</h3>
          <p class="text-sm text-gray-500">This user only has the default base user role.</p>
        </div>
      `
    }
  }

  resetRemovalButton(button) {
    const roleElement = button.closest('[data-role-id]')
    if (roleElement) {
      roleElement.style.opacity = '1'
      button.disabled = false
      button.innerHTML = `
        <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        Remove
      `
    }
  }

  // Handle role assignment form submission
  assignRole(event) {
    event.preventDefault()
    
    const form = event.target
    const formData = new FormData(form)
    const roleId = formData.get('role_id')
    
    if (!roleId) {
      this.showNotification('Please select a role to assign', 'error')
      return
    }

    this.setAssignmentLoadingState()
    this.executeRoleAssignment(form, formData, roleId)
  }

  setAssignmentLoadingState() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      const buttonText = this.submitButtonTarget.querySelector('.assign-role-text')
      const buttonLoading = this.submitButtonTarget.querySelector('.assign-role-loading')
      if (buttonText && buttonLoading) {
        buttonText.classList.add('hidden')
        buttonLoading.classList.remove('hidden')
      }
    }
  }

  async executeRoleAssignment(form, formData, roleId) {
    try {
      const newAction = form.action.replace('/0', `/${roleId}`)

      // Convert FormData to JSON
      const formObject = {}
      formData.forEach((value, key) => {
        formObject[key] = value
      })

      const response = await post(newAction, {
        body: formObject,
        responseKind: "json"
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.statusCode}`)
      }

      const data = await response.json

      if (data.success) {
        this.showNotification(data.message, 'success')
        this.toggleRoleAssignment() // Hide the form

        // Reload page to show new role with proper data
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else {
        this.showNotification(
          data.error || 'Failed to assign role. Please check that the user does not already have this role.',
          'error'
        )
      }
    } catch (error) {
      console.error('Error:', error)
      this.showNotification('An error occurred while assigning the role. Please try again.', 'error')
    } finally {
      this.resetAssignmentLoadingState()
    }
  }

  resetAssignmentLoadingState() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      const buttonText = this.submitButtonTarget.querySelector('.assign-role-text')
      const buttonLoading = this.submitButtonTarget.querySelector('.assign-role-loading')
      if (buttonText && buttonLoading) {
        buttonText.classList.remove('hidden')
        buttonLoading.classList.add('hidden')
      }
    }
  }

  // Admin action methods
  showAdminActionForm(event) {
    const action = event.currentTarget.dataset.actionType
    this.currentAdminAction = action
    
    this.adminActionReasonTarget.value = ''
    this.adminActionConfirmTarget.checked = false
    
    this.updateAdminActionDetails(action)
    this.adminActionFormTarget.classList.remove('hidden')
  }

  updateAdminActionDetails(action) {
    const userName = this.userNameValue
    let detailsHtml = ''

    if (action === 'promote') {
      detailsHtml = `
        <div class="p-3 bg-blue-50 rounded-md border border-blue-200">
          <p class="text-sm text-blue-800 font-medium">
            <svg class="w-4 h-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            You are about to grant admin privileges to ${userName}
          </p>
          <p class="text-xs text-blue-700 mt-1">
            This will allow them to manage other users, view applications, and access administrative features.
          </p>
        </div>
      `
    } else if (action === 'demote') {
      detailsHtml = `
        <div class="p-3 bg-amber-50 rounded-md border border-amber-200">
          <p class="text-sm text-amber-800 font-medium">
            <svg class="w-4 h-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            You are about to remove admin privileges from ${userName}
          </p>
          <p class="text-xs text-amber-700 mt-1">
            They will lose access to administrative features and return to standard user permissions.
          </p>
        </div>
      `
    }
    
    this.adminActionDetailsTarget.innerHTML = detailsHtml
  }

  hideAdminActionForm() {
    this.adminActionFormTarget.classList.add('hidden')
    this.currentAdminAction = null
  }

  async executeAdminAction() {
    if (!this.validateAdminAction()) {
      return
    }

    this.setAdminActionLoadingState()
    
    try {
      const endpoint = this.getAdminActionEndpoint()
      const response = await this.makeAdminActionRequest(endpoint)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.statusCode}`)
      }

      const data = await response.json

      if (data.success) {
        this.showNotification(data.message, 'success')
        this.hideAdminActionForm()

        // Reload page to update displays
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else {
        this.showNotification(data.error || 'Failed to update admin role', 'error')
      }
    } catch (error) {
      console.error('Error:', error)
      this.showNotification('An error occurred while updating the admin role. Please try again.', 'error')
    } finally {
      this.resetAdminActionLoadingState()
    }
  }

  validateAdminAction() {
    const reason = this.adminActionReasonTarget.value.trim()
    const confirmed = this.adminActionConfirmTarget.checked
    
    if (!reason) {
      this.showNotification('Please provide a reason for this change', 'error')
      this.adminActionReasonTarget.focus()
      return false
    }
    
    if (!confirmed) {
      this.showNotification('Please confirm you understand the implications', 'error')
      return false
    }
    
    return true
  }

  getAdminActionEndpoint() {
    if (this.currentAdminAction === 'promote') {
      return `/admin/users/${this.userIdValue}/promote_to_admin`
    } else if (this.currentAdminAction === 'demote') {
      return `/admin/users/${this.userIdValue}/demote_from_admin`
    }
    throw new Error('Invalid admin action')
  }

  async makeAdminActionRequest(endpoint) {
    return patch(endpoint, {
      body: { reason: this.adminActionReasonTarget.value.trim() },
      responseKind: "json"
    })
  }

  setAdminActionLoadingState() {
    const submitButton = this.element.querySelector('#admin-action-submit')
    if (submitButton) {
      submitButton.disabled = true
      this.originalSubmitText = submitButton.textContent
      submitButton.innerHTML = `
        <svg class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Processing...
      `
    }
  }

  resetAdminActionLoadingState() {
    const submitButton = this.element.querySelector('#admin-action-submit')
    if (submitButton && this.originalSubmitText) {
      submitButton.disabled = false
      submitButton.textContent = this.originalSubmitText
    }
  }

  // Notification system
  showNotification(message, type = 'info') {
    // Remove any existing notifications
    const existingNotifications = document.querySelectorAll('.notification-toast')
    existingNotifications.forEach(n => n.remove())
    
    // Create notification
    const notification = document.createElement('div')
    notification.className = `notification-toast fixed top-4 right-4 max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden z-50 transform transition-all duration-300 translate-x-full`
    
    const iconHtml = type === 'success' ? `
      <svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
      </svg>
    ` : `
      <svg class="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
      </svg>
    `
    
    notification.innerHTML = `
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            ${iconHtml}
          </div>
          <div class="ml-3 w-0 flex-1">
            <p class="text-sm font-medium text-gray-900">
              ${type === 'success' ? 'Success!' : 'Error'}
            </p>
            <p class="mt-1 text-sm text-gray-500">
              ${message}
            </p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button class="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onclick="this.closest('.notification-toast').remove()">
              <span class="sr-only">Close</span>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `
    
    document.body.appendChild(notification)
    
    // Animate in
    setTimeout(() => {
      notification.classList.remove('translate-x-full')
      notification.classList.add('translate-x-0')
    }, 100)
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      notification.classList.add('translate-x-full')
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove()
        }
      }, 300)
    }, 5000)
  }
}